import React, { useState } from 'react'
import './EmotionHistory.css'

const EmotionHistory = ({ history, onClear, isActive }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const getEmotionIcon = (emotionName) => {
    const icons = {
      happy: '😊',
      sad: '😢',
      angry: '😠',
      surprised: '😲',
      disgusted: '🤢',
      fearful: '😨',
      neutral: '😐'
    }
    return icons[emotionName] || '🤔'
  }

  const getEmotionColor = (emotionName) => {
    const colors = {
      happy: '#00ff88',
      sad: '#4a9eff',
      angry: '#ff4444',
      surprised: '#ffaa00',
      disgusted: '#8b5cf6',
      fearful: '#ff6b6b',
      neutral: '#cccccc'
    }
    return colors[emotionName] || '#cccccc'
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 1) {
      return 'Just now'
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60)
      return `${hours}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getEmotionStats = () => {
    if (history.length === 0) return null

    const emotionCounts = {}
    history.forEach(entry => {
      emotionCounts[entry.emotion] = (emotionCounts[entry.emotion] || 0) + 1
    })

    const mostFrequent = Object.entries(emotionCounts)
      .sort(([,a], [,b]) => b - a)[0]

    return {
      total: history.length,
      mostFrequent: mostFrequent[0],
      mostFrequentCount: mostFrequent[1]
    }
  }

  const stats = getEmotionStats()

  return (
    <div className="emotion-history">
      <div className="history-header">
        <div className="history-title-section">
          <h3 className="history-title">Emotion History</h3>
          {stats && (
            <span className="history-count">
              {stats.total} detection{stats.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="history-controls">
          {history.length > 0 && (
            <button
              onClick={onClear}
              className="clear-history-btn"
              aria-label="Clear emotion history"
              title="Clear history"
            >
              <span className="clear-icon">🗑️</span>
            </button>
          )}
          
          {history.length > 5 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="expand-history-btn"
              aria-label={isExpanded ? 'Collapse history' : 'Expand history'}
              title={isExpanded ? 'Show less' : 'Show more'}
            >
              <span className="expand-icon">
                {isExpanded ? '▲' : '▼'}
              </span>
            </button>
          )}
        </div>
      </div>

      {stats && (
        <div className="history-stats">
          <div className="stat-item">
            <span className="stat-label">Most Frequent:</span>
            <span 
              className="stat-value"
              style={{ color: getEmotionColor(stats.mostFrequent) }}
            >
              {getEmotionIcon(stats.mostFrequent)} {stats.mostFrequent.charAt(0).toUpperCase() + stats.mostFrequent.slice(1)}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Occurrences:</span>
            <span className="stat-value">{stats.mostFrequentCount}</span>
          </div>
        </div>
      )}

      {history.length === 0 ? (
        <div className="history-empty">
          <div className="empty-icon">📊</div>
          <h4>No Emotion History</h4>
          <p>
            {isActive 
              ? 'Emotion changes will appear here as they are detected'
              : 'Start the camera to begin tracking emotion changes'
            }
          </p>
        </div>
      ) : (
        <div className={`history-list ${isExpanded ? 'expanded' : ''}`}>
          {history
            .slice(0, isExpanded ? history.length : 5)
            .map((entry) => (
              <div key={entry.id} className="history-item fade-in">
                <div className="history-item-main">
                  <div 
                    className="history-emotion-icon"
                    style={{ color: getEmotionColor(entry.emotion) }}
                  >
                    {getEmotionIcon(entry.emotion)}
                  </div>
                  
                  <div className="history-item-details">
                    <div className="history-emotion-name">
                      {entry.emotion.charAt(0).toUpperCase() + entry.emotion.slice(1)}
                    </div>
                    <div className="history-confidence">
                      {entry.confidence}% confidence
                    </div>
                  </div>
                  
                  <div className="history-timestamp">
                    {formatTimestamp(entry.timestamp)}
                  </div>
                </div>
                
                <div className="history-confidence-bar">
                  <div 
                    className="history-confidence-fill"
                    style={{ 
                      width: `${entry.confidence}%`,
                      backgroundColor: getEmotionColor(entry.emotion)
                    }}
                  ></div>
                </div>
              </div>
            ))}
          
          {!isExpanded && history.length > 5 && (
            <div className="history-more">
              <button
                onClick={() => setIsExpanded(true)}
                className="show-more-btn"
              >
                Show {history.length - 5} more entries
              </button>
            </div>
          )}
        </div>
      )}

      <div className="history-info">
        <div className="info-item">
          <span className="info-icon">📈</span>
          <span className="info-text">Track your emotional patterns over time</span>
        </div>
        <div className="info-item">
          <span className="info-icon">🔄</span>
          <span className="info-text">History updates in real-time</span>
        </div>
      </div>
    </div>
  )
}

export default EmotionHistory 